/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {Children, MouseEvent, ReactNode} from 'react';
import Breadcrumbs, {BreadcrumbsProps} from '@material-ui/core/Breadcrumbs';
import {makeStyles} from '@material-ui/core/styles';
import omit from 'lodash.omit';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import Render from '../../render/Render';
import {toggleThemeStyle} from '../../theme/helpers';
import {dlsColor} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import {Color} from '../../types/types';

// defines the prop definitions for the Breadcrumb
export interface Breadcrumbs extends BreadcrumbsProps {
  children: ReactNode;
  icon?: JSX.Element;
  iconClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  height?: string;
  breadcrumbClass?: string;
  style?: GenericObject;
  color?: Color;
  maxItems?: number;
  itemsAfterCollapse?: number;
  itemsBeforeCollapse?: number;
  backLabel?: ReactNode;
  showBackLabel?: boolean;
}

// defines the styles used by the Breadcrumbs
export const useStyles = makeStyles(theme => ({
  breadcrumbRoot : {
    gap        : toggleThemeStyle('unset', '16px'),
    display    : 'flex',
    alignItems : toggleThemeStyle('center', 'baseline'),
    minHeight  : ({height}: {height?: string}) => height?.toString() || '70px',
    maxHeight  : '90px',
    '& span'   : {
      display : 'flex'
    },
    '&:hover' : {
      cursor : 'pointer'
    }
  },
  breadcrumbOl : {
    rowGap : '16px'
  },
  backButtonGroup : {
    gap            : '8px',
    top            : '8px',
    height         : '44px',
    display        : 'flex',
    padding        : '4px 8px',
    position       : 'relative',
    alignItems     : 'center',
    borderRadius   : '8px',
    justifyContent : 'center'
  },
  breadcrumbIcon : {
    '&:hover' : {
      cursor : 'pointer'
    }
  },
  backButtonText : {
    top           : '1px',
    color         : dlsColor('grey1'),
    fontSize      : '12px',
    position      : 'relative',
    fontFamily    : 'Inter',
    fontWeight    : 500,
    lineHeight    : '16px',
    letterSpacing : '0'
  },
  breadcrumbText : {
    top           : '1px',
    color         : dlsColor('linkBlue'),
    fontSize      : '14px',
    position      : 'relative',
    fontStyle     : 'italic',
    // the current theme's crumbs in Inter, whose italic is its own ...
    fontFamily    : toggleThemeStyle('Roboto', 'Inter'),
    fontWeight    : 500,
    lineHeight    : '16px',
    whiteSpace    : 'nowrap',
    letterSpacing : '0.5px',
    textTransform : 'capitalize'
  }
}));

/**
 * @Breadcrumb
 *
 * Defines the Breadcrumb component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 *   import Breadcrumb from '@ion/dls/src/components/breadcrumbs/Breadcrumbs';
 *   <Breadcrumb icon={} iconClick={}>
 *     {children}
 *   </Breadcrumb>
 * ```
 */
export const Breadcrumb = ({
  children,
  icon,
  iconClick,
  height,
  color,
  style,
  breadcrumbClass = '',
  maxItems = 3,
  itemsAfterCollapse = 1,
  itemsBeforeCollapse = 1,
  backLabel,
  showBackLabel = false,
  ...props
}: Breadcrumbs) => {
  const styleProps       = {height, key: undefined};
  const classes          = useStyles(styleProps);
  const breadcrumbsProps = children ? props : omit(props, ['classes']);
  const visibleChildren  = Children.toArray(children).filter(child => (
    typeof child === 'object' && 'props' in child ? Boolean(child.props?.children) : Boolean(child)
  ));

  const {
    isCurrent : isCurrentTheme
  } = useThemeContext();

  return (
    <div style={style}
      className={`${classes.breadcrumbRoot} ${breadcrumbClass}`}>
      <Render.If condition={icon && !showBackLabel}>
        <IconButton style={{all: 'unset', cursor: 'pointer'}}
          onClick={iconClick}
          color={color}
          icon={icon} />
      </Render.If>
      <Render.If condition={icon && showBackLabel}>
        <div className={classes.backButtonGroup}>
          <IconButton isControlButton
            controlButtonSize={24}
            controlButtonPadding={0}
            controlFrameSize={24}
            controlIconSize={24}
            onClick={iconClick}
            color={color}
            icon={icon} />
          <span className={classes.backButtonText}>
            {translate('common:editors.shared.back')}
          </span>
        </div>
      </Render.If>
      <Breadcrumbs maxItems={maxItems}
        aria-label='breadcrumb'
        itemsAfterCollapse={itemsAfterCollapse}
        itemsBeforeCollapse={itemsBeforeCollapse}
        {...(isCurrentTheme && {
          separator : '>',
          classes   : {ol: classes.breadcrumbOl}
        })}
        {...breadcrumbsProps}>
        {
          Boolean(icon) && showBackLabel && Boolean(backLabel) && (
            <span className={classes.breadcrumbText}>
              {backLabel}
            </span>
          )
        }
        {/* legacy keeps every crumb, as before - its crumb links (e.g. the
          side sheet's) carry their text as a prop, not as children, so the
          empty-crumb filter would drop them all ... */}
        {isCurrentTheme ? visibleChildren : children}
      </Breadcrumbs>
    </div>
  );
};

export default Breadcrumb;
